import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Globe2, ShieldCheck, Ship, Award, Boxes } from "lucide-react";
import { getCategories, getStats, getBlog, getProducts } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal, MaskLines } from "@/components/Reveal";
import { SectionHeading, Marquee, CTASection } from "@/components/shared";

const HERO = "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=1920&h=1200&fit=crop";

const CHAPTERS = [
  { n: "01", t: "Sourced with rigour", d: "We identify and audit producers across multiple international origins, negotiate directly, and take ownership of quality long before a container is sealed." },
  { n: "02", t: "Inspected independently", d: "Every lot is verified by SGS, Bureau Veritas or Intertek. A full Certificate of Analysis accompanies each shipment — no exceptions." },
  { n: "03", t: "Delivered with certainty", d: "From EXW to DDP, we manage freight, insurance and documentation so your cargo clears customs and arrives exactly as specified." },
];

const WHY = [
  { icon: Globe2, t: "Global origin network", d: "Direct relationships with vetted producers across Americas, Europe, Africa, GCC and Asia." },
  { icon: ShieldCheck, t: "Uncompromising QA", d: "Third-party inspection and full CoA on every consignment, aligned to your market's standards." },
  { icon: Ship, t: "End-to-end logistics", d: "FCL, LCL, reefer and flexitank shipping to every major port, plus turnkey DDP delivery." },
  { icon: Award, t: "UK-registered trust", d: "A regulated English counterparty with transparent contracts and documentation." },
];

const TESTIMONIALS = [];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ products: 100, categories: 17, countries: 90, years: 16 });
  const [posts, setPosts] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 160]);

  useEffect(() => {
    getCategories().then(setCats).catch(() => {});
    getStats().then(setStats).catch(() => {});
    getBlog().then((b) => setPosts(b.slice(0, 3))).catch(() => {});
    getProducts({ featured: true, limit: 8 }).then((d) => setFeatured(d.items)).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "WebPage",
    name: "Ingren Trading LLP | International Import & Export (UK)",
    url: process.env.REACT_APP_BACKEND_URL,
    description: "International B2B trading and supply company sourcing agricultural products, edible oils, metals, plastics, dairy, animal feed and more for buyers worldwide.",
    isPartOf: { "@type": "WebSite", name: "Ingren Trading LLP", url: process.env.REACT_APP_BACKEND_URL },
  };

  return (
    <>
      <Seo title="Ingren Trading LLP | Global B2B Trading & International Supply"
        description="Ingren Trading LLP is an international B2B trading and supply partner sourcing edible oils, metals, sugar, rice, pulses, dairy, poultry, plastics, animal feed and more for buyers worldwide. Request a quote."
        path="/" jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden bg-[hsl(var(--navy))]">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src={HERO} alt="Container ship at a global trading port at dawn" width="1920" height="1200" data-hero className="w-full h-[120%] object-cover opacity-35 img-treat" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))] via-[hsl(var(--navy))]/60 to-transparent" />
        <div className="hero-grid absolute inset-0 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 w-full pb-20 pt-32">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="eyebrow text-[hsl(var(--brass-2))] mb-6">
            International B2B Trading Company · United Kingdom
          </motion.div>
          <h1 className="font-display text-[hsl(var(--paper))] text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight max-w-5xl">
            <MaskLines lines={["Global B2B Trading", "& International"]} delay={0.3} />
            <MaskLines lines={["Supply."]} delay={0.55} className="text-[hsl(var(--brass-2))]" />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 text-white/75 max-w-xl text-base sm:text-lg leading-relaxed">
            Ingren Trading LLP provides international B2B sourcing and supply solutions across a diverse range of commodities, agricultural products, food products, raw materials and industrial products — connecting buyers and suppliers worldwide.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/products" data-testid="hero-products" className="inline-flex items-center gap-2 bg-[hsl(var(--brass))] text-[hsl(var(--ink))] px-7 py-3.5 font-semibold hover:bg-[hsl(var(--brass-2))] transition-colors">
              Explore Products <ArrowUpRight size={18} />
            </Link>
            <Link to="/contact" data-testid="hero-quote" className="inline-flex items-center gap-2 border border-white/25 text-[hsl(var(--paper))] px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors">
              Request a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[hsl(var(--ink))] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[[stats.products + "+", "Products supplied"], [stats.categories + "", "Product categories"], ["5", "Incoterms offered"], ["24h", "Quotation response"]].map(([v, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div className="font-display text-4xl md:text-5xl text-[hsl(var(--brass-2))]">{v}</div>
              <div className="mt-1 text-white/60 text-sm">{l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Edible Oils", "Metals", "Sugar", "Rice & Grains", "Pulses", "Dairy", "Poultry", "Plastics", "Animal Feed", "Fertilizer"]} />

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <SectionHeading eyebrow="What we trade" title="Seventeen categories. One reliable partner." />
          <Link to="/products" className="text-sm font-semibold text-[hsl(var(--navy))] flex items-center gap-1.5 link-underline w-fit">View all products <ArrowUpRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.06}>
              <Link to={`/products/${c.slug}`} data-testid={`cat-card-${c.slug}`} className="group block relative overflow-hidden bg-[hsl(var(--navy))] h-72">
                <img src={c.image} alt={`${c.name} export supplier`} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 img-treat" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))] to-transparent" />
                <div className="relative h-full p-6 flex flex-col justify-end">
                  <div className="font-mono-l text-[10px] text-[hsl(var(--brass-2))] mb-2">{String(c.product_count).padStart(2, "0")} PRODUCTS</div>
                  <div className="font-display text-2xl text-[hsl(var(--paper))]">{c.name}</div>
                  <div className="text-white/65 text-sm mt-1 line-clamp-2">{c.tagline}</div>
                  <div className="mt-4 flex items-center gap-1.5 text-[hsl(var(--brass-2))] text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                    Browse category <ArrowUpRight size={15} />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading eyebrow="Featured products" title="Hand-picked lines, ready to ship." />
            <Link to="/products" className="text-sm font-semibold text-[hsl(var(--navy))] flex items-center gap-1.5 link-underline w-fit">Browse all products <ArrowUpRight size={16} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.06}>
                <Link to={`/products/${p.category_slug}/${p.slug}`} data-testid={`featured-${p.slug}`} className="group block bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--brass))] transition-colors overflow-hidden">
                  <div className="h-48 overflow-hidden relative">
                    <img src={p.image} alt={`${p.name} supplier`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-treat" />
                    <span className="absolute top-3 left-3 font-mono-l text-[10px] bg-[hsl(var(--brass))] text-[hsl(var(--ink))] px-2 py-1">{p.category}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg text-[hsl(var(--navy))] group-hover:text-[hsl(var(--brass))] transition-colors">{p.name}</h3>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">Origin: {p.origin}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}


      {/* MANIFESTO CHAPTERS */}
      <section className="bg-[hsl(var(--navy))] relative grain">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
          <SectionHeading light eyebrow="Our method" title="How we turn global sourcing into a dependable supply chain." />
          <div className="mt-16 grid lg:grid-cols-3 gap-px bg-white/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="bg-[hsl(var(--navy))] p-9 h-full">
                  <div className="font-display text-6xl text-[hsl(var(--brass))]/40">{c.n}</div>
                  <h3 className="font-display text-2xl text-[hsl(var(--paper))] mt-5">{c.t}</h3>
                  <p className="text-white/65 mt-3 leading-relaxed text-sm">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <SectionHeading eyebrow="Why choose us" title="The reliability of a UK house, the reach of a global trader." center />
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.08}>
              <div className="border border-[hsl(var(--border))] p-7 h-full hover:border-[hsl(var(--brass))] transition-colors group bg-white">
                <w.icon className="text-[hsl(var(--brass))]" size={28} />
                <h3 className="font-display text-xl mt-5 text-[hsl(var(--navy))]">{w.t}</h3>
                <p className="text-[hsl(var(--muted-foreground))] text-sm mt-2 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS removed pending verified client references */}
      {TESTIMONIALS.length > 0 && (
      <section className="bg-[hsl(var(--secondary))]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
          <SectionHeading eyebrow="Customer testimonials" title="Trusted by importers on five continents." />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure className="bg-white border border-[hsl(var(--border))] p-8 h-full flex flex-col">
                  <Boxes className="text-[hsl(var(--brass))]" size={26} />
                  <blockquote className="font-display text-xl text-[hsl(var(--navy))] leading-snug mt-5 flex-1">“{t.q}”</blockquote>
                  <figcaption className="mt-6 text-sm"><span className="font-semibold text-[hsl(var(--navy))]">{t.n}</span><br /><span className="text-[hsl(var(--muted-foreground))]">{t.c}</span></figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* BLOG PREVIEW */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <SectionHeading eyebrow="Insights" title="From the trade desk." />
          <Link to="/blog" className="text-sm font-semibold text-[hsl(var(--navy))] flex items-center gap-1.5 link-underline">All articles <ArrowUpRight size={16} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link to={`/blog/${p.slug}`} className="group block">
                <div className="overflow-hidden h-52"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-treat" /></div>
                <div className="eyebrow text-[hsl(var(--accent))] mt-4">{p.category}</div>
                <h3 className="font-display text-xl text-[hsl(var(--navy))] mt-2 group-hover:text-[hsl(var(--brass))] transition-colors">{p.title}</h3>
                <p className="text-[hsl(var(--muted-foreground))] text-sm mt-2 line-clamp-2">{p.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
